import React, { useState } from 'react';
import {
  Typography,
  Button,
  Box,
  Avatar,
  Chip,
  Container,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Logout as LogoutIcon, Person as PersonIcon, Business as BusinessIcon, Build as BuildIcon } from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import appConfig from '../config/app';

const Header = ({ user }) => {
  const auth = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const open = Boolean(anchorEl);

  // Simplified logout handler - easiest way to logout
  const handleLogout = async () => {
    try {
      setLogoutConfirmOpen(false);
      setAnchorEl(null);

      console.log('Starting logout process...');

      // Set logout flag to prevent auto-re-authentication
      sessionStorage.setItem('user_logged_out', 'true');

      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      sessionStorage.setItem('user_logged_out', 'true'); // Re-set after clear

      // Manual logout using Cognito's logout endpoint with correct parameters
      const clientId = auth.settings?.client_id || '2qq5ghqsquifrg3vj12p2ktj4t';
      const logoutUri = encodeURIComponent(window.location.origin);
      const cognitoDomain = 'https://eu-central-1jrodmdgrr.auth.eu-central-1.amazoncognito.com';

      // Use logout_uri parameter (not post_logout_redirect_uri)
      const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;

      console.log('Redirecting to Cognito logout:', logoutUrl);

      // Direct redirect to Cognito logout endpoint
      window.location.href = logoutUrl;

    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: simple redirect to home with logout flag
      sessionStorage.setItem('user_logged_out', 'true');
      window.location.href = window.location.origin + '?logout=true';
    }
  };

  const handleLogoutClick = () => {
    setLogoutConfirmOpen(true);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutConfirmClose = () => {
    setLogoutConfirmOpen(false);
  };

  const getUserTypeIcon = (userType) => {
    switch (userType) {
      case 'serviceman':
        return <BuildIcon />;
      case 'customer':
        return <BusinessIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getUserTypeColor = (userType) => {
    switch (userType) {
      case 'serviceman':
        return 'success';
      case 'customer':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ mb: 3 }}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box sx={{ px: 3, py: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={3}>
                <Avatar
  sx={{
    width: 56,
    height: 56,
    bgcolor: '#126F42',
    color: 'white',
    fontSize: '1.5rem',
    fontWeight: 'bold',
  }}
>
                  {appConfig.app.title.charAt(0)}
                </Avatar>
                <Box>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      color: '#1e293b',
                      fontWeight: 'bold',
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      lineHeight: 1.2,
                    }}
                  >
                    {appConfig.app.title}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: '#475569',
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                  >
                    Welcome, {user.name || user.email}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip
                  icon={getUserTypeIcon(user.user_type)}
                  label={user.user_type === 'serviceman' ? 'SERVICEMAN' : 'CUSTOMER'}
                  color={getUserTypeColor(user.user_type)}
                  variant="filled"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.75rem',

                  }}
                />

                <Box>
                  <IconButton
                    onClick={handleMenuClick}
                    sx={{
                      color: '#475569',
                      bgcolor: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      '&:hover': {
                        bgcolor: '#e2e8f0',
                        borderColor: '#cbd5e1',
                      },
                    }}
                  >
                    <PersonIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleMenuClose}
                    PaperProps={{
                      elevation: 8,
                      sx: {
                        borderRadius: 2,
                        minWidth: 200,
                        mt: 1,
                      },
                    }}
                  >
                    <MenuItem disabled>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {user.name || 'User'}
                        </Typography>

                        {user.email && user.email !== user.name && (
                          <Typography variant="body2" color="text.secondary">
                            {user.email}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogoutClick}>
                      <LogoutIcon sx={{ mr: 1 }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
      <Dialog
        open={logoutConfirmOpen}
        onClose={handleLogoutConfirmClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 300,
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <LogoutIcon color="warning" />
            Confirm Logout
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to logout? You will be redirected to the login page.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleLogoutConfirmClose} color="inherit" variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            color="error"
            variant="contained"
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;